import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Input, Select, DatePicker } from "@/shared/ui";
import type { RangeValue } from "@/shared/ui";
import type { Locale } from "@/shared/utils";
import type { TransactionType } from "../../../types";

interface TransactionFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  typeFilter: TransactionType;
  onTypeChange: (value: TransactionType) => void;
  dateRange: { from: Date; to: Date };
  onDateRangeChange: (from: Date, to: Date) => void;
  locale: Locale;
  disabled?: boolean;
}

export default function TransactionFilters({
  search,
  onSearchChange,
  typeFilter,
  onTypeChange,
  dateRange,
  onDateRangeChange,
  locale,
  disabled = false,
}: TransactionFiltersProps) {
  const { t } = useTranslation("BankingPage");
  const [pendingRange, setPendingRange] = useState<RangeValue | null>(null);

  const typeOptions = [
    { label: t("filters.typeAll"), value: "ALL" as TransactionType },
    { label: t("filters.typeCredit"), value: "CREDIT" as TransactionType },
    { label: t("filters.typeDebit"), value: "DEBIT" as TransactionType },
  ];

  function handleRangeChange(value: RangeValue | null) {
    if (value && value.start && value.end) {
      onDateRangeChange(value.start, value.end);
      setPendingRange(null);
    } else {
      setPendingRange(value);
    }
  }

  return (
    <div className="flex w-full justify-start items-center gap-3 p-1">
      <div className="flex-1">
        <Input
          label={t("filters.search")}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t("filters.search")}
          disabled={disabled}
        />
      </div>
      <div className="w-56">
        <Select<TransactionType>
          label={t("filters.type")}
          options={typeOptions}
          value={typeFilter}
          onChange={(v) => onTypeChange(v ?? "ALL")}
          disabled={disabled}
        />
      </div>
      <DatePicker
        mode="range"
        label={t("filters.dateRange")}
        value={pendingRange ?? { start: dateRange.from, end: dateRange.to }}
        onChange={handleRangeChange}
        maxDate={new Date()}
        locale={locale}
        disabled={disabled}
      />
    </div>
  );
}

